import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CssBaseline, SwipeableDrawer } from '@mui/material';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import MobileBottomNav from './components/MobileBottomNav';
import MobileDrawerContent from './components/MobileDrawerContent';
import { useIsMobile } from './hooks/useIsMobile';
import OfflineBanner from './components/OfflineBanner';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import DeletedClientsPage from './pages/DeletedClientsPage';
import ClientsListPageCached from './pages/ClientsListPageCached';
import ClientDetailPage from './pages/ClientDetailPage';
import GroupsManagementPage from './pages/GroupsManagementPage';
import GroupDetailPage from './pages/GroupDetailPage';
import MinimalGroupPrintPage from './pages/MinimalGroupPrintPage';
import TransactionsPage from './pages/TransactionsPage';
import FunctionalGroupsPage from './pages/FunctionalGroupsPage';
import YearlyFeeManagementPage from './pages/YearlyFeeManagementPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import UpdateProfilePage from './pages/UpdateProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/ProtectedRoute';
import MinimalPrintPage from './pages/MinimalPrintPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ProductsPage from './pages/ProductsPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ClientOrdersPage from './pages/ClientOrdersPage';
import NewOrderPage from './pages/NewOrderPage';
import NewOrderPageLegacy from './pages/NewOrderPageLegacy';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDetailPage from './pages/SupplierDetailPage';
import SupplierMinimalPrintPage from './pages/SupplierMinimalPrintPage';
import ProductsPrintPage from './pages/ProductsPrintPage';
import CashFlowPage from './pages/CashFlowPage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import QuotationsPage from './pages/QuotationsPage';
import NewQuotationPage from './pages/NewQuotationPage';
import QuotationDetailPage from './pages/QuotationDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import NotificationRulesPage from './pages/NotificationRulesPage';
import InventoryActionPage from './pages/InventoryActionPage';
import KioskInventoryPage from './pages/KioskInventoryPage';

import InstallPrompt from './components/InstallPrompt';

import { useUser } from './contexts/UserContext';

// Preload images at module level to cache them immediately
const imagesToPreload = ['/logo.png'];
imagesToPreload.forEach((src) => {
  const img = new Image();
  img.src = src;
});

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

function App() {
  const location = useLocation();
  const isKioskRoute = location.pathname.startsWith('/kiosk/');
  const showSidebar = location.pathname !== "/login" && !isKioskRoute;
  const { userProfile } = useUser();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Kiosk routes render outside the app shell: no navbar, no sidebar,
  // no install prompt, no offline banner, no mobile drawer. The page
  // is a standalone warehouse inventory management station.
  if (isKioskRoute) {
    return (
      <QueryClientProvider client={queryClient}>
        <CssBaseline />
        <Routes>
          <Route path="/kiosk/scanner/:token" element={<KioskInventoryPage />} />
          <Route path="/kiosk/*" element={<NotFoundPage />} />
        </Routes>
      </QueryClientProvider>
    );
  }

  // Function to get allowed roles for a page
  const getRolesForPage = (pageName) => {
    // Default fallback roles if config not available
    const defaultRoles = {
      'DashboardPage': ['manager'],
      'ClientsListPageCached': ['manager', 'employee'],
      'DeletedClientsPage': ['manager'],
      'MinimalPrintPage': ['manager', 'employee'],
      'ClientDetailPage': ['manager', 'employee'],
      'GroupsManagementPage': ['manager'],
      'GroupDetailPage': ['manager'],
      'MinimalGroupPrintPage': ['manager'],
      'TransactionsPage': ['manager'],
      'FunctionalGroupsPage': ['manager'],
      'YearlyFeeManagementPage': ['manager'],
      'SuppliersPage': ['manager', 'employee'],
      'SupplierDetailPage': ['manager', 'employee'],
      'SupplierMinimalPrintPage': ['manager', 'employee'],
      'ProductsPrintPage': ['manager', 'employee'],
      'CashFlowPage': ['manager'],
      'EmployeesPage': ['manager'],
    };

    // Define pages that should always be accessible (default pages)
    const alwaysAllowedPages = [
      'DashboardPage',
      'SuppliersPage',
      'SupplierDetailPage',
      'SupplierMinimalPrintPage',
      'ClientsListPageCached',
      'ClientDetailPage',
      'ChangePasswordPage',
      'UpdateProfilePage',
      'MinimalPrintPage',
      'ProductsPrintPage',
      'CashFlowPage',
    ];

    // Get the list of allowed pages from userProfile
    const allowedPages = userProfile?.ui_config?.allowed_pages || [];
    
    // Always allow access to user profile and password change
    if (pageName === 'ChangePasswordPage' || pageName === 'UpdateProfilePage') {
      return ['manager', 'employee'];
    }
    
    // Check if it's a default page that should always be allowed
    const isDefaultPage = alwaysAllowedPages.includes(pageName);
    
    // If page is not a default page and not in allowed pages, return empty array (no access)
    if (!isDefaultPage && !allowedPages.includes(pageName)) {
      return [];
    }

    // If no profile or no config, use defaults
    if (!userProfile || !userProfile.ui_config || !userProfile.ui_config.pages_configs) {
      return defaultRoles[pageName] || ['manager'];
    }

    // Get page config
    const pageConfig = userProfile.ui_config.pages_configs[pageName];
    
    // If page config exists and has allowed_roles, use them
    if (pageConfig && pageConfig.allowed_roles) {
      return pageConfig.allowed_roles;
    }
    
    // Fallback to defaults
    return defaultRoles[pageName] || ['manager'];
  };
  
  return (
    <QueryClientProvider client={queryClient}>
    <InstallPrompt />
    <OfflineBanner />
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <Navbar />

      <Box sx={{ display: 'flex', flexGrow: 1, mt: 0 }}>
        {showSidebar && !isMobile && <Sidebar />}
        <Box component="main" sx={{
          flexGrow: 1,
          px: { xs: 0, sm: 2, md: 3 },
          py: { xs: 1, sm: 2, md: 3 },
          pb: { xs: showSidebar ? 8 : 1, sm: showSidebar ? 8 : 2, md: 3 },
          backgroundColor: '#f4f4f9',
          minWidth: 0,
          maxWidth: '100%',
          // Mobile only: clip horizontal overflow so MUI Grid container
          // negative margins (e.g., spacing={3} = -24px each side) cannot
          // escape past the viewport edge. Desktop unchanged. clip (vs
          // hidden) does NOT create a new formatting context, so sticky
          // positioning still works correctly.
          overflowX: { xs: 'clip', md: 'visible' },
          transition: 'all 0.3s ease',
        }}>
          <Routes>
            {/* Always include login route */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* New Routes for Settings */}
            <Route path="/change-password" element={
              <ProtectedRoute allowedRoles={['manager', 'employee']}>
                <ChangePasswordPage />
              </ProtectedRoute>
            } />
            
            <Route path="/update-profile" element={
              <ProtectedRoute allowedRoles={['manager', 'employee']}>
                <UpdateProfilePage />
              </ProtectedRoute>
            } />
            
            {/* Always define all routes but control access via ProtectedRoute */}
            <Route path="/dashboard" element={
              <ProtectedRoute allowedRoles={getRolesForPage('DashboardPage')}>
                <DashboardPage />
              </ProtectedRoute>
            } />

            <Route path="/clients" element={
              <ProtectedRoute allowedRoles={getRolesForPage('ClientsListPageCached')}>
                <ClientsListPageCached />
              </ProtectedRoute>
            } />

            <Route path="/clients/deleted" element={
              <ProtectedRoute allowedRoles={getRolesForPage('DeletedClientsPage')}>
                <DeletedClientsPage />
              </ProtectedRoute>
            } />

            <Route path="/clients/minimal-print" element={
              <ProtectedRoute allowedRoles={getRolesForPage('MinimalPrintPage')}>
                <MinimalPrintPage />
              </ProtectedRoute>
            } />

            <Route path="/client/:business_id" element={
              <ProtectedRoute allowedRoles={getRolesForPage('ClientDetailPage')}>
                <ClientDetailPage />
              </ProtectedRoute>
            } />

            <Route path="/groups" element={
              <ProtectedRoute allowedRoles={getRolesForPage('GroupsManagementPage')}>
                <GroupsManagementPage />
              </ProtectedRoute>
            } />

            <Route path="/group/:group_id" element={
              <ProtectedRoute allowedRoles={getRolesForPage('GroupDetailPage')}>
                <GroupDetailPage />
              </ProtectedRoute>
            } />

            <Route path="/group/:group_id/minimal-print" element={
              <ProtectedRoute allowedRoles={getRolesForPage('MinimalGroupPrintPage')}>
                <MinimalGroupPrintPage />
              </ProtectedRoute>
            } />

            <Route path="/transactions" element={
              <ProtectedRoute allowedRoles={getRolesForPage('TransactionsPage')}>
                <TransactionsPage />
              </ProtectedRoute>
            } />

            <Route path="/cashflow" element={
              <ProtectedRoute allowedRoles={getRolesForPage('CashFlowPage')}>
                <CashFlowPage />
              </ProtectedRoute>
            } />

            <Route path="/employees" element={
              <ProtectedRoute allowedRoles={getRolesForPage('EmployeesPage')}>
                <EmployeesPage />
              </ProtectedRoute>
            } />

            <Route path="/employees/:id" element={
              <ProtectedRoute allowedRoles={getRolesForPage('EmployeesPage')}>
                <EmployeeDetailPage />
              </ProtectedRoute>
            } />

            <Route path="/functional-groups" element={
              <ProtectedRoute allowedRoles={getRolesForPage('FunctionalGroupsPage')}>
                <FunctionalGroupsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/yearly-fee" element={
              <ProtectedRoute allowedRoles={getRolesForPage('YearlyFeeManagementPage')}>
                <YearlyFeeManagementPage />
              </ProtectedRoute>
            } />

            <Route path="/products" element={
              <ProtectedRoute allowedRoles={getRolesForPage('ProductsPage')}>
                <ProductsPage />
              </ProtectedRoute>
            } />

            <Route path="/products/print" element={
              <ProtectedRoute allowedRoles={getRolesForPage('ProductsPrintPage')}>
                <ProductsPrintPage />
              </ProtectedRoute>
            } />

            <Route path="/product/:product_id" element={
              <ProtectedRoute allowedRoles={getRolesForPage('ProductDetailPage')}>
                <ProductDetailPage />
              </ProtectedRoute>
            } />

            <Route path="/orders" element={
              <ProtectedRoute allowedRoles={getRolesForPage('OrdersPage')}>
                <OrdersPage />
              </ProtectedRoute>
            } />

            <Route path="/order/new" element={
              <ProtectedRoute allowedRoles={getRolesForPage('NewOrderPage')}>
                <NewOrderPage />
              </ProtectedRoute>
            } />

            <Route path="/order/new/legacy" element={
              <ProtectedRoute allowedRoles={getRolesForPage('NewOrderPage')}>
                <NewOrderPageLegacy />
              </ProtectedRoute>
            } />

            <Route path="/order/:order_id" element={
              <ProtectedRoute allowedRoles={getRolesForPage('OrderDetailPage')}>
                <OrderDetailPage />
              </ProtectedRoute>
            } />

            <Route path="/quotations" element={
              <ProtectedRoute allowedRoles={getRolesForPage('OrdersPage')}>
                <QuotationsPage />
              </ProtectedRoute>
            } />

            <Route path="/quotation/new" element={
              <ProtectedRoute allowedRoles={getRolesForPage('OrdersPage')}>
                <NewQuotationPage />
              </ProtectedRoute>
            } />

            <Route path="/quotation/:id" element={
              <ProtectedRoute allowedRoles={getRolesForPage('OrdersPage')}>
                <QuotationDetailPage />
              </ProtectedRoute>
            } />

              <Route path="/client/:business_id/orders" element={
                <ProtectedRoute allowedRoles={getRolesForPage('ClientOrdersPage')}>
                  <ClientOrdersPage />
                </ProtectedRoute>
              } />

              <Route path="/suppliers" element={
                <ProtectedRoute allowedRoles={getRolesForPage('SuppliersPage')}>
                  <SuppliersPage />
                </ProtectedRoute>
              } />

              <Route path="/suppliers/minimal-print" element={
                <ProtectedRoute allowedRoles={getRolesForPage('SupplierMinimalPrintPage')}>
                  <SupplierMinimalPrintPage />
                </ProtectedRoute>
              } />

              <Route path="/supplier/:supplier_id" element={
                <ProtectedRoute allowedRoles={getRolesForPage('SupplierDetailPage')}>
                  <SupplierDetailPage />
                </ProtectedRoute>
              } />
              
            {/* Notification routes (managers only, accessed via navbar bell) */}
            <Route path="/notifications" element={
              <ProtectedRoute allowedRoles={['manager']}>
                <NotificationsPage />
              </ProtectedRoute>
            } />
            <Route path="/notification-rules" element={
              <ProtectedRoute allowedRoles={['manager']}>
                <NotificationRulesPage />
              </ProtectedRoute>
            } />

            <Route path="/inventory-action" element={
              <ProtectedRoute allowedRoles={getRolesForPage('InventoryActionPage')}>
                <InventoryActionPage />
              </ProtectedRoute>
            } />

            {/* Always include 404 route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Box>
      </Box>

      {/* Mobile drawer navigation */}
      {showSidebar && isMobile && (
        <SwipeableDrawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
            },
          }}
        >
          <MobileDrawerContent
            userProfile={userProfile}
            onNavigate={() => setDrawerOpen(false)}
          />
        </SwipeableDrawer>
      )}

      {/* Mobile bottom navigation */}
      {showSidebar && isMobile && (
        <MobileBottomNav onMoreClick={() => setDrawerOpen(true)} />
      )}
    </Box>
    </QueryClientProvider>
  );
}

export default App;